import { appColors } from '@/src/theme'
import { Input, InputGroup, InputLeftElement, type InputProps } from '@chakra-ui/react'
import { FocusLock } from '@components'
import { useTranslation } from 'react-i18next'
import { RiSearchLine } from 'react-icons/ri'

interface ISearchBoxProps {
  /** Unique identifier for this SearchBox instance. This ID is used in conjunction with `focusId` to manage and lock focus programmatically from the parent component. */
  id?: number
  /** The ID of the SearchBox that is currently designated to retain focus. By comparing `id` with `focusId`, the component can determine whether it should actively trap focus, enabling controlled focus management, useful in composite UI components like dropdowns, modals, or dynamic forms where focus can be handled differently than desired. */
  focusId?: number
  value?: string
  onChange?: (value: string) => void
  selectedBorderColor?: 'blue' | 'black' | 'green'
  width?: string
  maxWidth?: string
  height?: string
  inputProps?: InputProps
}

export const SearchBox = ({
  id = 0,
  focusId,
  value,
  onChange,
  selectedBorderColor,
  width,
  maxWidth,
  height,
  inputProps,
}: ISearchBoxProps) => {
  const { t } = useTranslation()
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    onChange?.(e.target.value)
  }

  const borderColorClassName =
    selectedBorderColor === 'blue' ? 'border-color-blue' : selectedBorderColor === 'black' ? 'border-color-black' : ''
  return (
    <InputGroup w='full'>
      <InputLeftElement h='full' style={{ width: '50px' }} pointerEvents='none'>
        <RiSearchLine color={appColors.gray['400']} />
      </InputLeftElement>
      {/* FocusLock component will prevent input from loosing focus when inside elements that gain focus on mount/re-render. Thus preventing issues of inputs inside menus or popovers */}
      <FocusLock restoreFocus isDisabled={id !== focusId}>
        <Input
          w={width || 'full'}
          maxW={maxWidth}
          key={id}
          padding={0}
          style={{ paddingLeft: '44px', height: height ?? '36px', fontSize: 14, paddingTop: '2px', color: 'gray.100' }}
          className={`${borderColorClassName} search-box-focus`}
          value={value}
          onChange={handleChange}
          placeholder={t('common.search')}
          _placeholder={{ color: 'gray.500', fontSize: 14 }}
          backgroundColor={appColors.gray['100']}
          borderWidth='1px'
          borderColor={'gray.100'}
          onBlur={e => {
            e.stopPropagation()
          }}
          _hover={{
            backgroundColor: appColors.gray['200'],
            color: 'gray.900',
          }}
          {...inputProps}
        />
      </FocusLock>
    </InputGroup>
  )
}
